<template>
  <div class="books-collection-view">
    <!-- Success Message Toast -->
    <v-snackbar
      v-model="showSnackbar"
      :timeout="3500"
      color="success"
      location="top right"
      rounded="lg"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-check-circle-outline</v-icon>
        {{ message }}
      </div>
    </v-snackbar>

    <!-- Header Section -->
    <div class="view-header mb-6">
      <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
        <div class="d-flex align-center ga-3">
          <div class="section-icon-badge" style="background: rgba(16, 185, 129, 0.15)">
            <v-icon color="#10b981" size="26">mdi-book-open-page-variant</v-icon>
          </div>
          <div>
            <h1 class="text-h4 font-weight-bold">Books Collection</h1>
            <p class="text-caption text-medium-emphasis mb-0">
              {{ displayPosts.length }} title{{ displayPosts.length === 1 ? '' : 's' }} in library
            </p>
          </div>
        </div>

        <!-- Header Actions -->
        <div class="d-flex align-center flex-wrap ga-2">
          <!-- Favorite Filter Toggle -->
          <v-btn
            :variant="showFavoritesOnly ? 'flat' : 'tonal'"
            :color="showFavoritesOnly ? 'success' : undefined"
            size="small"
            class="btn-modern"
            :prepend-icon="showFavoritesOnly ? 'mdi-heart' : 'mdi-heart-outline'"
            @click="toggleFavorites"
          >
            Favorites
            <v-chip
              size="x-small"
              class="ml-1.5 font-weight-bold"
              :color="showFavoritesOnly ? 'white' : 'success'"
              variant="flat"
            >
              {{ favoritesCount }}
            </v-chip>
          </v-btn>

          <!-- Wishlist Link -->
          <v-btn
            to="/books/wishlist"
            variant="tonal"
            size="small"
            class="btn-modern"
            prepend-icon="mdi-bookmark-outline"
          >
            Wishlist
          </v-btn>

          <!-- Add Book Primary CTA -->
          <v-btn
            to="/books/add-post"
            color="success"
            class="btn-modern text-white font-weight-bold"
            prepend-icon="mdi-plus"
          >
            Add Book
          </v-btn>
        </div>
      </div>

      <!-- Controls & Filter Toolbar -->
      <div class="filter-toolbar glass-card pa-3 mb-4">
        <v-row align="center" dense>
          <!-- Search Field -->
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="searchQuery"
              placeholder="Filter by title, author, category..."
              density="compact"
              hide-details
              prepend-inner-icon="mdi-magnify"
              clearable
              variant="outlined"
            />
          </v-col>

          <!-- Sort Select -->
          <v-col cols="6" sm="3" md="3">
            <v-select
              v-model="sortBy"
              :items="sortOptions"
              item-title="label"
              item-value="value"
              density="compact"
              hide-details
              variant="outlined"
              label="Sort by"
            />
          </v-col>

          <!-- View Mode Toggle -->
          <v-col cols="6" sm="3" md="5" class="d-flex justify-end align-center ga-1">
            <v-btn-toggle
              v-model="viewMode"
              mandatory
              density="compact"
              color="success"
              variant="outlined"
              rounded="lg"
            >
              <v-btn value="shelf" title="Shelf View">
                <v-icon size="18">mdi-view-carousel-outline</v-icon>
              </v-btn>
              <v-btn value="grid" title="Grid View">
                <v-icon size="18">mdi-grid</v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
      </div>

      <!-- Category Filter Pills Bar -->
      <div class="filter-pills-bar">
        <v-chip
          v-for="cat in categoryOptions"
          :key="cat"
          :variant="choice === cat ? 'flat' : 'tonal'"
          :color="choice === cat ? 'success' : undefined"
          class="filter-pill"
          size="small"
          @click="choice = cat"
        >
          {{ cat }}
        </v-chip>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="py-12 text-center">
      <v-progress-circular indeterminate color="success" size="48" width="4" />
      <div class="text-body-2 text-medium-emphasis mt-3">Loading books collection...</div>
    </div>

    <!-- Error State -->
    <v-alert v-else-if="error" type="error" class="mb-6" variant="tonal">{{ error }}</v-alert>

    <!-- Content: Shelves View -->
    <template v-else-if="processedPosts.length && viewMode === 'shelf'">
      <RowCategory
        v-for="category in visibleCategories"
        :key="category"
        :category="category"
        :posts="processedPosts"
      />
    </template>

    <!-- Content: Dense Poster Grid View -->
    <template v-else-if="processedPosts.length && viewMode === 'grid'">
      <div class="poster-dense-grid">
        <div
          v-for="post in processedPosts"
          :key="post.id"
          class="grid-poster-item"
        >
          <router-link
            :to="{ name: 'BookDetail', params: { id: post.id } }"
            class="poster-card poster-card--books d-block text-decoration-none"
          >
            <div class="grid-poster-container">
              <v-img
                :src="posterSrc(post)"
                :aspect-ratio="2 / 3"
                cover
                class="rounded-lg w-100"
              >
                <template #placeholder>
                  <div class="d-flex flex-column align-center justify-center fill-height fallback-poster">
                    <v-icon size="36" color="#10b981">mdi-book-open-page-variant</v-icon>
                    <span class="text-caption text-center px-2 mt-1 text-truncate w-100 font-weight-medium">
                      {{ post.title }}
                    </span>
                  </div>
                </template>
              </v-img>

              <div class="poster-badges-top">
                <div v-if="post.rating && Number(post.rating) > 0" class="rating-badge">
                  <v-icon size="12" color="amber" class="mr-0.5">mdi-star</v-icon>
                  <span>{{ post.rating }}</span>
                </div>
              </div>

              <div class="poster-overlay-bottom">
                <div class="poster-title text-truncate font-weight-bold">{{ post.title }}</div>
                <div class="poster-subtitle d-flex justify-space-between align-center">
                  <span v-if="post.author" class="text-truncate">{{ post.author }}</span>
                  <v-chip size="x-small" variant="tonal" color="white" class="ml-auto">
                    {{ post.category }}
                  </v-chip>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </template>

    <!-- Empty State -->
    <v-card v-else class="glass-card pa-12 text-center my-6">
      <v-icon size="64" color="success" class="mb-4">mdi-book-remove-outline</v-icon>
      <h3 class="text-h5 font-weight-bold mb-2">
        {{ showFavoritesOnly ? 'No Favorites Found' : 'No Books Found' }}
      </h3>
      <p class="text-body-1 text-medium-emphasis mb-6 max-w-sm mx-auto">
        <template v-if="showFavoritesOnly">
          No favorite books yet. Open any book and click Favorite.
        </template>
        <template v-else-if="searchQuery">
          No books matched your search "{{ searchQuery }}".
        </template>
        <template v-else>
          Your bookshelf is empty. Add your first book to get started!
        </template>
      </p>
      <v-btn
        to="/books/add-post"
        color="success"
        size="large"
        class="btn-modern text-white font-weight-bold"
        prepend-icon="mdi-plus"
      >
        Add Your First Book
      </v-btn>
    </v-card>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import RowCategory from '@/components/books/RowCategory.vue'
import BooksAPI from '@/services/books'
import { CATEGORIES } from '@/config/books'
import { posterSrc } from '@/utils/bookForm'

export default {
  name: 'BooksHomeView',
  components: { RowCategory },
  setup() {
    const route = useRoute()
    const posts = ref([])
    const loading = ref(true)
    const error = ref('')
    const choice = ref('All')
    const showFavoritesOnly = ref(false)
    const searchQuery = ref('')
    const sortBy = ref('created_desc')
    const viewMode = ref('shelf')
    const message = ref(route.query.message || '')
    const showSnackbar = ref(!!route.query.message)

    const categoryOptions = ['All', ...CATEGORIES]

    const sortOptions = [
      { label: 'Recently Added', value: 'created_desc' },
      { label: 'Highest Rated', value: 'rating_desc' },
      { label: 'Publish Year', value: 'year_desc' },
      { label: 'Title (A-Z)', value: 'title_asc' },
    ]

    const postMarked = (post) => {
      const value = post.marked
      if (value == null || value === '') return '0'
      return String(value)
    }

    const toggleFavorites = () => {
      showFavoritesOnly.value = !showFavoritesOnly.value
    }

    const favoritesCount = computed(() => {
      return posts.value.filter((post) => postMarked(post) === '1').length
    })

    const displayPosts = computed(() => {
      if (showFavoritesOnly.value) {
        return posts.value.filter((post) => postMarked(post) === '1')
      }
      return posts.value.filter((post) => {
        const marked = postMarked(post)
        return marked === '0' || marked === '1'
      })
    })

    const processedPosts = computed(() => {
      let list = displayPosts.value.slice()

      if (choice.value !== 'All') {
        list = list.filter((p) => p.category === choice.value)
      }

      if (searchQuery.value && searchQuery.value.trim()) {
        const q = searchQuery.value.toLowerCase().trim()
        list = list.filter((p) => {
          return (
            (p.title && p.title.toLowerCase().includes(q)) ||
            (p.author && p.author.toLowerCase().includes(q)) ||
            (p.category && p.category.toLowerCase().includes(q))
          )
        })
      }

      if (sortBy.value === 'created_desc') {
        list.sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0))
      } else if (sortBy.value === 'rating_desc') {
        list.sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0))
      } else if (sortBy.value === 'year_desc') {
        list.sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0))
      } else if (sortBy.value === 'title_asc') {
        list.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
      }

      return list
    })

    const visibleCategories = computed(() => {
      if (choice.value !== 'All') return [choice.value]
      const populated = new Set(processedPosts.value.map((p) => p.category))
      return CATEGORIES.filter((c) => populated.has(c))
    })

    const loadPosts = async () => {
      loading.value = true
      error.value = ''
      try {
        const { data } = await BooksAPI.getAllPost()
        posts.value = data.slice().reverse()
      } catch (err) {
        error.value = err.response?.data?.detail?.message || err.message || 'Failed to load books'
      } finally {
        loading.value = false
      }
    }

    watch(
      () => route.query.message,
      (value) => {
        if (value) {
          message.value = value
          showSnackbar.value = true
        }
      },
    )

    onMounted(loadPosts)

    return {
      posts,
      displayPosts,
      processedPosts,
      loading,
      error,
      choice,
      showFavoritesOnly,
      toggleFavorites,
      favoritesCount,
      searchQuery,
      sortBy,
      sortOptions,
      viewMode,
      message,
      showSnackbar,
      categoryOptions,
      visibleCategories,
      posterSrc,
    }
  },
}
</script>

<style scoped>
.books-collection-view {
  max-width: 1360px;
  margin: 0 auto;
}

.section-icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
}

.filter-toolbar {
  border-radius: 16px !important;
}

.poster-dense-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}

@media (max-width: 600px) {
  .poster-dense-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
  }
}

.grid-poster-container {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  background: rgba(128, 128, 128, 0.08);
}

.fallback-poster {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(52, 211, 153, 0.1));
}

.poster-badges-top {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 2;
}

.rating-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
}

.poster-overlay-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px 8px 8px 8px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 60%, transparent 100%);
  color: #fff;
  z-index: 2;
}

.poster-title {
  font-size: 0.8rem;
  line-height: 1.2;
}

.poster-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.max-w-sm {
  max-width: 420px;
}
</style>

